import {
  ATTRIBUTES,
  LIMITS,
  SYMBOLS,
  dateRangePlaceHolder
} from "src/config/constants/genericConstants";
import {
  formatCurrencyOrNumber,
  getFormatedStoreName
} from "src/utils/genericFunctions";
import ToastManager from "src/components/Toast";
import {
  getFormatedDate,
  getFormattedDate,
  isDateDifferenceWithinLimit
} from "src/utils/getDates";
import {
  BasicDataFilterConfig,
  BasicDataFilterConfigProp,
  CashierData,
  InputDependentDropdownData,
  RegisterData,
  TenderTypeData,
  TlnFilter
} from "./interface";
import { HeaderRule } from "src/components/GoTable/interface";
import stringToNumber from "src/utils/stringToNumber";

export const getPath = (
  label: string,
  translationKey: string,
  to?: string
) => ({
  label,
  translationKey,
  to
});

export const getBreadCrumData = (currentReport: string) => [
  getPath("", "reports.reportsTab", "/reports"),
  getPath("", currentReport)
];

export const getAmountBetweenUpdatedKey = (name: string) =>
  name === ATTRIBUTES.FROM
    ? ATTRIBUTES.TOTAL_AMOUNT_MIN
    : ATTRIBUTES.TOTAL_AMOUNT_MAX;

export const getUpdatedItemType = (formData: TlnFilter, value: string) => {
  let updatedData = [...(formData?.lineItemType || [])];
  if (updatedData.includes(value)) {
    updatedData = updatedData.filter((eachData: string) => eachData !== value);
  } else {
    updatedData.push(value);
  }
  return updatedData;
};
export const getDate = ({ fromDate, toDate }: TlnFilter) => {
  const isDateSelected = [fromDate, toDate].every(checkInstanceOfDate);
  return isDateSelected
    ? `${getFormatedDate(fromDate)} - ${getFormatedDate(toDate)}`
    : dateRangePlaceHolder;
};

export const checkInstanceOfDate = (date: Date | string) =>
  date instanceof Date || date.length > 0;

export const checkIsDisplayDisabled = (
  apiFilters: TlnFilter,
  formData: TlnFilter
) =>
  formData.tenderTypes?.length < 1 ||
  formData.cashiers?.length < 1 ||
  formData.registers?.length < 1 ||
  !checkInstanceOfDate(formData.fromDate) ||
  !checkInstanceOfDate(formData.toDate) ||
  JSON.stringify(apiFilters) === JSON.stringify(formData);

export const conditionalConfigUpdate = (
  config: BasicDataFilterConfig,
  transactionTotal: boolean
) => {
  const filteredData = config.inputs.filter(
    ({ id }: { id: string }) =>
      ![ATTRIBUTES.LINE_ITEM_AMOUNT, ATTRIBUTES.AMOUNT_BETWEEN].includes(id)
  );
  const transactionIndex = filteredData.findIndex(
    ({ id }: { id: string }) => id === ATTRIBUTES.TRANSACTION_TOTAL
  );
  const conditionalData = transactionTotal
    ? config.conditionalInputs.lineItemAmount
    : config.conditionalInputs.amountBetween;
  filteredData.splice(transactionIndex + 1, 0, conditionalData);
  config.inputs = [...filteredData];
};

export const getBasicDataFilterConfig = ({
  formData,
  storeName,
  storeId,
  apiFilters,
  dropdowns,
  callback
}: BasicDataFilterConfigProp): BasicDataFilterConfig => {
  const {
    updateItemAmount,
    updateAmountBetween,
    updateDate,
    updateTransactionNum,
    updateItemType,
    updateUpc,
    updateTransactionTotal,
    handleClear,
    handleSelectDropDown,
    handleSubmit
  } = callback;
  const {
    registerOptions,
    cashierOptions,
    tenderTypesOptions,
    itemTypesOptions
  } = dropdowns;
  return {
    conditionalInputs: {
      lineItemAmount: {
        id: ATTRIBUTES.LINE_ITEM_AMOUNT,
        component: "textField",
        defaultValue: formData.lineItemAmount || "",
        label: ATTRIBUTES.LINE_ITEM_AMOUNT,
        placeHolder: "Line Item Amount",
        isMandatoryField: false,
        isCapitalRequired: false,
        isDisabled: false,
        handleChange: updateItemAmount
      },
      amountBetween: {
        id: ATTRIBUTES.AMOUNT_BETWEEN,
        component: "textFieldRange",
        label: ATTRIBUTES.AMOUNT_BETWEEN,
        handleChange: updateAmountBetween,
        minAmount: {
          id: ATTRIBUTES.FROM,
          defaultValue: (formData.totalAmountMin as string) || "",
          placeHolder: "From"
        },
        maxAmount: {
          id: ATTRIBUTES.TO,
          defaultValue: (formData.totalAmountMax as string) || "",
          placeHolder: "To"
        },
        errorMessage: formData.errorMessage
      }
    },
    inputs: [
      {
        id: ATTRIBUTES.DATE,
        component: ATTRIBUTES.DATE,
        defaultValue: getDate(formData),
        label: ATTRIBUTES.DATE,
        isMandatoryField: true,
        submitHandler: updateDate,
        showQuickDateSelection: false
      },
      {
        id: ATTRIBUTES.STORE,
        component: "nonEditable",
        defaultValue: getFormatedStoreName({ storeName, storeId }),
        label: ATTRIBUTES.STORE,
        isMandatoryField: true
      },
      {
        id: ATTRIBUTES.REGISTERS,
        component: "multiSelect",
        defaultValue: "",
        label: ATTRIBUTES.REGISTER,
        isMandatoryField: true,
        selectedValues: formData.registers || [],
        allValuesLabel: "All Register",
        placeholder: "Select Register",
        options: registerOptions,
        handleSubmit: (_name: unknown, selectedValues: string[]) =>
          handleSelectDropDown(ATTRIBUTES.REGISTERS, selectedValues)
      },
      {
        id: ATTRIBUTES.CASHIERS,
        component: "multiSelect",
        defaultValue: "",
        label: ATTRIBUTES.CASHIER,
        isMandatoryField: true,
        selectedValues: formData.cashiers,
        allValuesLabel: "All Cashier",
        placeholder: "Select Cashier",
        options: cashierOptions,
        handleSubmit: (_name: unknown, selectedValues: string[]) =>
          handleSelectDropDown(ATTRIBUTES.CASHIERS, selectedValues)
      },
      {
        id: ATTRIBUTES.TRANSACTION_ID,
        component: "textField",
        defaultValue: formData.transactionId || "",
        label: "transaction#",
        placeHolder: "Transaction #",
        isMandatoryField: false,
        isCapitalRequired: false,
        isDisabled: false,
        handleChange: updateTransactionNum
      },
      {
        id: ATTRIBUTES.UPC_OR_ITEMID,
        component: "textField",
        defaultValue: formData.upcOrItemId || "",
        label: "upcCodeOrItemId",
        placeHolder: "UPC Code/Item#",
        isMandatoryField: false,
        isCapitalRequired: false,
        isDisabled: false,
        handleChange: updateUpc
      },
      {
        id: ATTRIBUTES.TRANSACTION_TOTAL,
        component: "checkBoxLabel",
        value: formData.transactionTotal,
        checkBoxLabel: ATTRIBUTES.TRANSACTION_TOTAL,
        submitHandler: updateTransactionTotal
      },
      {
        id: ATTRIBUTES.LINE_ITEM_AMOUNT,
        component: "textField",
        defaultValue: formData.lineItemAmount || "",
        label: ATTRIBUTES.LINE_ITEM_AMOUNT,
        placeHolder: "Line Item Amount",
        isMandatoryField: false,
        isCapitalRequired: false,
        isDisabled: false,
        handleChange: updateItemAmount
      },
      {
        id: ATTRIBUTES.TENDER_TYPES,
        component: "multiSelect",
        defaultValue: "",
        label: "tenderType",
        isMandatoryField: true,
        selectedValues: formData.tenderTypes,
        allValuesLabel: "All Tender Types",
        placeholder: "Select Tender Type",
        options: tenderTypesOptions,
        handleSubmit: (_name: unknown, selectedValues: string[]) =>
          handleSelectDropDown(ATTRIBUTES.TENDER_TYPES, selectedValues)
      },
      {
        id: ATTRIBUTES.LINE_ITEM_TYPE,
        component: "multiSelectCheckBox",
        label: "itemType",
        isMandatoryField: false,
        selectedType: formData.lineItemType || [],
        submitHandler: updateItemType,
        types: itemTypesOptions
      }
    ],
    formHandlers: [
      {
        id: ATTRIBUTES.CLEAR,
        component: "button",
        label: ATTRIBUTES.CLEAR,
        handleClick: handleClear
      },
      {
        id: ATTRIBUTES.DISPLAY,
        component: "button",
        label: ATTRIBUTES.DISPLAY,
        isDisabled: checkIsDisplayDisabled(apiFilters, formData),
        handleClick: handleSubmit
      }
    ]
  };
};

export const getTranslatedHeader = (label: string, t: Function) =>
  t(`transactionLogReport.table.header.${label}`);

export const getHeaderRules = (t: Function): HeaderRule[] => [
  {
    id: ATTRIBUTES.DATE,
    isSortable: true,
    sortBy: ATTRIBUTES.DATE,
    align: "center",
    label: getTranslatedHeader(ATTRIBUTES.DATE, t),
    headerStyleOverrides: { width: "100px", p: "9px" },
    dataCellStyleOverrides: {
      p: "9px",
      textAlign: "center"
    }
  },
  {
    id: ATTRIBUTES.TIME,
    isSortable: true,
    sortBy: ATTRIBUTES.TIME,
    align: "center",
    label: getTranslatedHeader(ATTRIBUTES.TIME, t),
    headerStyleOverrides: { width: "100px", p: "9px" },
    dataCellStyleOverrides: {
      p: "9px",
      textAlign: "center"
    }
  },
  {
    id: ATTRIBUTES.REGISTER_NAME,
    isSortable: true,
    sortBy: ATTRIBUTES.REGISTER,
    align: "center",
    label: getTranslatedHeader(ATTRIBUTES.REGISTER, t),
    headerStyleOverrides: { width: "100px", p: "9px" },
    dataCellStyleOverrides: {
      p: "9px",
      textAlign: "center"
    }
  },
  {
    id: ATTRIBUTES.TRANSACTION,
    isSortable: true,
    sortBy: ATTRIBUTES.TRANSACTION,
    align: "center",
    label: getTranslatedHeader("transaction#", t),
    headerStyleOverrides: { width: "100px", p: "9px" },
    dataCellStyleOverrides: {
      p: "9px",
      textAlign: "center"
    }
  },
  {
    id: ATTRIBUTES.CASHIER,
    isSortable: true,
    sortBy: ATTRIBUTES.CASHIER,
    align: "center",
    label: getTranslatedHeader(ATTRIBUTES.CASHIER, t),
    headerStyleOverrides: { width: "100px", p: "9px" },
    dataCellStyleOverrides: {
      p: "9px",
      textAlign: "center"
    }
  },
  {
    id: ATTRIBUTES.TOTAL_AMOUNT,
    isSortable: true,
    sortBy: ATTRIBUTES.TOTAL_AMOUNT,
    align: "center",
    label: getTranslatedHeader(ATTRIBUTES.TOTAL_AMOUNT, t),
    headerStyleOverrides: { width: "100px", p: "9px" },
    dataCellStyleOverrides: {
      p: "9px",
      textAlign: "center"
    }
  },
  {
    id: ATTRIBUTES.NEW_TLR_DISPLAY,
    label: getTranslatedHeader(ATTRIBUTES.DISPLAY, t),
    align: "center",
    headerStyleOverrides: { width: "100px", p: "9px", align: "center" },
    dataCellStyleOverrides: {
      p: "9px",
      textAlign: "center"
    }
  }
];

export const getUpdatedSelectedIds = (
  options: any[],
  selectedOptions: string[],
  loadmasterIds = false
) => {
  const updatedSelectedOption: string[] = [];
  const optionIds = options?.map(
    ({ id }: { id: string | number }) => id as string
  );
  selectedOptions.forEach((eachData: string) => {
    if (optionIds.includes(eachData)) {
      updatedSelectedOption.push(eachData);
    }
  });
  return loadmasterIds ? optionIds : updatedSelectedOption;
};

export const handleDollarValue = (value: string | number) => {
  const hasDecimal = `${value}`.indexOf(".") !== -1;
  const [beforeDecimal, afterdecimal] = `${value}`.split(".");
  let result = formatCurrencyOrNumber(`$${beforeDecimal}`, 0, 0);
  if (result === "$0") {
    if (value === SYMBOLS.DOLLAR) {
      result = "";
    }
    if (value === SYMBOLS.MINUS) {
      result = SYMBOLS.MINUS;
    }
  }
  if (hasDecimal) {
    result += `.${
      afterdecimal ? `${afterdecimal}`.replace(/\D+/g, "").slice(0, 2) : ""
    }`;
  }
  return result;
};

export const generateBooleanDynamicObj = (data: string[]) =>
  data.reduce((storeData: Record<string, boolean>, eachType: string) => {
    storeData[eachType] ??= true;
    return storeData;
  }, {});

export const updateTotalMinMaxAmount = (payload: TlnFilter) => {
  if (!payload?.totalAmountMax) {
    delete payload.totalAmountMax;
  } else {
    payload.totalAmountMax = stringToNumber(payload.totalAmountMax);
  }
  if (!payload?.totalAmountMin) {
    delete payload.totalAmountMin;
  } else {
    payload.totalAmountMin = stringToNumber(payload.totalAmountMin);
  }
};

export const updateConditionalLineAmount = (payload: TlnFilter) => {
  if (`${payload.lineItemAmount}`?.length < 1) {
    delete payload.lineItemAmount;
  } else {
    payload.lineItemAmount = stringToNumber(payload.lineItemAmount);
  }
};
export const updateTransactionTotalCondtionalPayload = (payload: TlnFilter) => {
  if (payload.transactionTotal) {
    delete payload.totalAmountMax;
    delete payload.totalAmountMin;
    updateConditionalLineAmount(payload);
  } else {
    delete payload.lineItemAmount;
    updateTotalMinMaxAmount(payload);
  }
};
export const getSubmitPayload = (
  formData: TlnFilter,
  dropdowns: BasicDataFilterConfigProp["dropdowns"]
) => {
  const payload = { ...formData };
  const itemTypes = generateBooleanDynamicObj(payload.lineItemType);
  updateTransactionTotalCondtionalPayload(payload);
  payload.cashiers?.length === dropdowns.cashierOptions?.length &&
    delete payload?.cashiers;
  payload.tenderTypes?.length === dropdowns.tenderTypesOptions?.length &&
    delete payload?.tenderTypes;
  payload.registers?.length === dropdowns.registerOptions?.length &&
    delete payload.registers;
  delete payload.lineItemType;
  delete payload.transactionTotal;
  payload.fromDate = getFormattedDate(formData.fromDate);
  payload.toDate = getFormattedDate(formData.toDate);
  stringToNumber(payload?.transactionId || 0) < 1 &&
    delete payload.transactionId;
  payload.upcOrItemId = `${payload?.upcOrItemId || ""}`;
  payload.upcOrItemId?.length < 1 && delete payload.upcOrItemId;
  return { ...payload, ...itemTypes };
};

export const formValidation = (formData: TlnFilter) => {
  if (
    !formData.transactionId &&
    stringToNumber(formData.totalAmountMin) >
      stringToNumber(formData.totalAmountMax)
  ) {
    return false;
  }

  return true;
};

export const getFormatedDropdowns = (
  parentOptions: RegisterData[] | CashierData[] | TenderTypeData[],
  selectedParent: string[],
  childKey: string
) => {
  let parentsChilds = parentOptions;
  if (selectedParent?.length > 0) {
    parentsChilds = parentOptions.filter(
      (item: { [key: string]: string | object | number }) =>
        selectedParent?.includes(String(item.id))
    );
  }
  let nestedChilds: object[] = [];
  parentsChilds?.forEach((eachParentsChilds: any) => {
    nestedChilds = [...nestedChilds, ...(eachParentsChilds?.[childKey] || [])];
  });
  return nestedChilds;
};

export const formatDependentDropdownData = (
  data: InputDependentDropdownData[]
): RegisterData[] =>
  Object.values(
    data.reduce((registers, { register, cashier, tenderType }) => {
      const reg = (registers[register] ??= {
        id: register,
        name: register,
        cashier: {}
      });
      const cashierData = (reg.cashier[cashier] ??= {
        id: cashier,
        name: cashier,
        tenderType: []
      });
      if (
        !cashierData.tenderType.some(
          (type: { name: string }) => type.name === tenderType
        )
      ) {
        cashierData.tenderType.push({ id: tenderType, name: tenderType });
      }
      return registers;
    }, {} as Record<string, { id: string; name: string; cashier: Record<string, CashierData> }>)
  ).map((reg: any) => ({ ...reg, cashier: Object.values(reg?.cashier) }));

export const getUniqueDropdowns = (
  masterDropdowns: RegisterData[],
  formData: TlnFilter
) => {
  const cashiers = getFormatedDropdowns(
    masterDropdowns,
    formData.registers,
    ATTRIBUTES.CASHIER
  );
  const tenders = getFormatedDropdowns(
    cashiers as CashierData[],
    formData.cashiers,
    ATTRIBUTES.TENDER_TYPE
  );
  const uniqueData = (data: { id: string }[]) =>
    Array.from(
      new Map(data.map((item: { id: string }) => [item.id, item])).values()
    );
  return {
    registers: uniqueData(masterDropdowns),
    cashiers: uniqueData(cashiers as { id: string }[]),
    tenders: uniqueData(tenders as { id: string }[])
  };
};

export const dateExceedLimitToastContent = (t: Function) => ({
  variant: "info",
  title: t("transactionLogReport.validation.dateRangeLimitTitle"),
  message: t("transactionLogReport.validation.dateRangeLimitMessage")
});

export const tlogDateRangeValidation = (
  fromDate: string,
  toDate: string,
  t: Function
) => {
  if (
    !isDateDifferenceWithinLimit(
      fromDate,
      toDate,
      LIMITS.TRANSACTION_LOG_REPORT_DATE_RANGE_LIMIT
    )
  ) {
    ToastManager.toast(dateExceedLimitToastContent(t));
    return true;
  }
  return false;
};
